import { currentSessionSnapshot, isSessionSnapshotCurrent, refreshSession } from './session-refresh'
import { useAuthStore } from './stores/auth.store'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:6300/api/v1'

type HeadersMap = Record<string, string>

interface RequestConfig extends Omit<RequestInit, 'headers'> {
  params?: Record<string, string | number | boolean | undefined>
  headers?: HeadersMap
}

type ApiErrorShape = {
  error?: {
    code?: string
    message?: string
    details?: unknown
  }
}

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

export const isNoContentStatus = (status: number): boolean => status === 204 || status === 205

const asApiErrorShape = (value: unknown): ApiErrorShape => {
  if (!isObjectRecord(value)) return {}
  const error = value['error']
  if (!isObjectRecord(error)) return {}

  return {
    error: {
      code: typeof error['code'] === 'string' ? error['code'] : undefined,
      message: typeof error['message'] === 'string' ? error['message'] : undefined,
      details: error['details'],
    },
  }
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private getHeaders(extra?: HeadersMap): HeadersMap {
    const headers: HeadersMap = {
      'Content-Type': 'application/json',
      ...(extra ?? {}),
    }

    const token = useAuthStore.getState().accessToken
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    return headers
  }

  private buildUrl(
    path: string,
    params?: Record<string, string | number | boolean | undefined>,
  ): string {
    const url = new URL(`${this.baseUrl}${path}`)
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.set(key, String(value))
        }
      })
    }
    return url.toString()
  }

  // Endpoints auth publics : un 401 ici = credentials invalides, PAS une session expirée
  private static readonly AUTH_PUBLIC_PATHS = [
    '/auth/login',
    '/auth/register',
    '/auth/verify-email',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/resend-verification',
  ]

  private isAuthPublicPath(path: string): boolean {
    return ApiClient.AUTH_PUBLIC_PATHS.some((p) => path.endsWith(p))
  }

  private async handleResponse<T>(response: Response, requestPath?: string): Promise<T> {
    if (response.status === 401) {
      // Sur les endpoints auth publics, on ne tente PAS de refresh
      // → on laisse passer le message d'erreur backend (ex: "Email ou mot de passe incorrect")
      if (requestPath && this.isAuthPublicPath(requestPath)) {
        let raw: unknown
        try {
          raw = await response.json()
        } catch {
          throw new ApiError(401, 'AUTH_ERROR', "Erreur d'authentification")
        }
        const shaped = asApiErrorShape(raw)
        throw new ApiError(
          401,
          shaped.error?.code ?? 'AUTH_ERROR',
          shaped.error?.message ?? 'Identifiants incorrects',
          shaped.error?.details,
        )
      }

      // 401 of a protected endpoint after the only permitted retry.
      throw new ApiError(401, 'SESSION_EXPIRED', 'Session expirée. Reconnecte-toi.')
    }

    // DELETE / 204 No Content : réponse OK sans body JSON.
    // Sans ce garde-fou, les mutations void (ex: retirer une note) sont traitées
    // comme des erreurs côté front malgré un status HTTP 204 réussi.
    if (isNoContentStatus(response.status)) {
      return undefined as T
    }

    // Patch X: gérer les réponses non-JSON (502, 503, HTML error pages)
    let raw: unknown
    try {
      raw = await response.json()
    } catch {
      throw new ApiError(
        response.status,
        'SERVER_ERROR',
        response.status >= 500
          ? 'Le serveur est temporairement indisponible. Réessaie dans quelques instants.'
          : `Erreur inattendue (${response.status})`,
      )
    }

    if (!response.ok) {
      const shaped = asApiErrorShape(raw)
      throw new ApiError(
        response.status,
        shaped.error?.code ?? 'UNKNOWN_ERROR',
        shaped.error?.message ?? this.getDefaultErrorMessage(response.status),
        shaped.error?.details,
      )
    }

    return raw as T
  }

  /** Patch X: messages d'erreur FR par défaut selon le status HTTP */
  private getDefaultErrorMessage(status: number): string {
    switch (status) {
      case 400:
        return 'Données invalides. Vérifie les champs du formulaire.'
      case 403:
        return "Accès refusé. Tu n'as pas les permissions nécessaires."
      case 404:
        return 'Ressource introuvable.'
      case 409:
        return 'Conflit — cette ressource existe déjà.'
      case 422:
        return 'Données non valides.'
      case 429:
        return 'Trop de requêtes. Attends un moment avant de réessayer.'
      default:
        return status >= 500
          ? 'Erreur serveur. Réessaie dans quelques instants.'
          : 'Erreur inattendue.'
    }
  }

  private async runWithRetry<T>(path: string, perform: () => Promise<Response>): Promise<T> {
    const started = currentSessionSnapshot()
    const doFetch = async (): Promise<Response> => {
      try {
        return await perform()
      } catch {
        throw new ApiError(0, 'NETWORK_ERROR', 'Erreur réseau. Vérifie ta connexion internet.')
      }
    }

    const first = await doFetch()
    if (first.status !== 401 || this.isAuthPublicPath(path)) {
      if (!isSessionSnapshotCurrent(started)) {
        throw new ApiError(409, 'SESSION_CHANGED', 'Session modifiée pendant la requête.')
      }
      const value = await this.handleResponse<T>(first, path)
      if (!isSessionSnapshotCurrent(started)) {
        throw new ApiError(409, 'SESSION_CHANGED', 'Session modifiée pendant la requête.')
      }
      return value
    }

    if (useAuthStore.getState().authPending) {
      throw new ApiError(401, 'AUTH_PENDING', 'Connexion en cours ou session refusée.')
    }

    if (!isSessionSnapshotCurrent(started)) {
      const now = currentSessionSnapshot()
      // An earlier 401 can arrive after another request already refreshed.
      // Retry with the newer token only when the authenticated user is still
      // the SAME one. Logout/login and account switches never reuse old data.
      if (
        !started.userId ||
        now.userId !== started.userId ||
        !useAuthStore.getState().isAuthenticated ||
        !now.token
      ) {
        throw new ApiError(409, 'SESSION_CHANGED', 'Session modifiée pendant la requête.')
      }
    } else if (!(await refreshSession())) {
      // refreshSession owns terminal 401/403 revocation. Contention, network
      // errors and server outages must not turn a recoverable refresh failure
      // into logout in this caller after the coordinator preserved the session.
      if (isSessionSnapshotCurrent(started)) {
        throw new ApiError(
          409,
          'REFRESH_UNAVAILABLE',
          'Renouvellement de session temporairement indisponible. Réessaie.',
        )
      }
      throw new ApiError(401, 'SESSION_EXPIRED', 'Session expirée. Reconnecte-toi.')
    }

    const retryContext = currentSessionSnapshot()
    if (!retryContext.token || useAuthStore.getState().authPending) {
      throw new ApiError(409, 'SESSION_CHANGED', 'Session modifiée pendant la requête.')
    }
    const retry = await doFetch()
    if (!isSessionSnapshotCurrent(retryContext)) {
      throw new ApiError(409, 'SESSION_CHANGED', 'Session modifiée pendant la requête.')
    }
    // Never refresh a second time for this logical API request, even if its
    // retry is 401. A second cookie rotation risks server-side replay revoke.
    if (retry.status === 401) useAuthStore.getState().logout()
    const value = await this.handleResponse<T>(retry, path)
    if (!isSessionSnapshotCurrent(retryContext)) {
      throw new ApiError(409, 'SESSION_CHANGED', 'Session modifiée pendant la requête.')
    }
    return value
  }

  private async requestWithRetry<T>(
    method: string,
    path: string,
    body?: unknown,
    config?: RequestConfig,
  ): Promise<T> {
    const url = this.buildUrl(path, config?.params)
    return this.runWithRetry(path, () =>
      fetch(url, {
        ...config,
        method,
        headers: this.getHeaders(config?.headers),
        credentials: 'include',
        body: body === undefined ? undefined : JSON.stringify(body),
      }),
    )
  }

  async get<T>(path: string, config?: RequestConfig): Promise<T> {
    return this.requestWithRetry<T>('GET', path, undefined, config)
  }

  async post<T>(path: string, body?: unknown, config?: RequestConfig): Promise<T> {
    return this.requestWithRetry<T>('POST', path, body, config)
  }

  async patch<T>(path: string, body?: unknown, config?: RequestConfig): Promise<T> {
    return this.requestWithRetry<T>('PATCH', path, body, config)
  }

  async delete<T>(path: string, config?: RequestConfig): Promise<T> {
    return this.requestWithRetry<T>('DELETE', path, undefined, config)
  }

  /** Upload multipart */
  async upload<T>(path: string, file: File, fieldName = 'file'): Promise<T> {
    const formData = new FormData()
    formData.append(fieldName, file)

    return this.runWithRetry<T>(path, () => {
      const token = useAuthStore.getState().accessToken
      return fetch(`${this.baseUrl}${path}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
        body: formData,
      })
    })
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export class RetryableError extends Error {
  constructor() {
    super('Retryable')
    this.name = 'RetryableError'
  }
}

/** Extract `data` from a standard `{ success, data }` API response envelope. */
export function unwrapResponse<T>(res: unknown): T {
  if (res && typeof res === 'object' && 'success' in res && 'data' in res) {
    return (res as { success: boolean; data: T }).data
  }
  return res as T
}

export const api = new ApiClient(API_BASE)
